import { startIdleTracking, getActiveMsSince } from './idleTracker';
import { getBackendURL } from '../components/env';
import { getSessionId, getSessionStartedAt } from './sessionId';

const BATCH_SIZE = 25;
// Max events sent per HTTP request. Reuses BATCH_SIZE so the two knobs never
// drift apart: a chunk this size can never legitimately hit the backend's
// MAX_BATCH_SIZE=100 (413) or the Fetch spec's 64 KiB keepalive body cap.
export const FLUSH_CHUNK = BATCH_SIZE;
const FLUSH_INTERVAL_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 60000;
export const MAX_BUFFER = 200;
const authReadyListeners = new Set();

let buffer = [];
let flushTimer = null;
let heartbeatTimer = null;
let lastHeartbeatAt = null;
let listenersAttached = false;
let _authContext = null; // { token, schema }
let _sessionStartedAt = null;

function _startHeartbeat() {
  if (heartbeatTimer) return;
  lastHeartbeatAt = Date.now();
  heartbeatTimer = setInterval(() => {
    if (!_authContext) return;
    const now = Date.now();
    const from = lastHeartbeatAt || _sessionStartedAt || now;
    const active_ms = getActiveMsSince(from, now);
    lastHeartbeatAt = now;
    if (active_ms > 0) {
      trackUserEvent('session_heartbeat', {
        duration_ms: active_ms,
        metadata: { interval_ms: HEARTBEAT_INTERVAL_MS },
      });
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function _stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  lastHeartbeatAt = null;
}

export function forceHeartbeat() {
  if (!_authContext) return;
  const now = Date.now();
  const from = lastHeartbeatAt || _sessionStartedAt || now;
  const active_ms = getActiveMsSince(from, now);
  lastHeartbeatAt = now;
  if (active_ms > 0) {
    trackUserEvent('session_heartbeat', {
      duration_ms: active_ms,
      metadata: { interval_ms: HEARTBEAT_INTERVAL_MS, forced: true },
    });
  }
}

export function setAuthContext(ctx) {
  if (!ctx || !ctx.token || !ctx.schema) {
    console.warn('[user_events_debug] setAuthContext cleared (missing token or schema)', {
      hasCtx: !!ctx, hasToken: !!ctx?.token, hasSchema: !!ctx?.schema,
    });
    _authContext = null;
    _stopHeartbeat();
    return;
  }
  const isFreshSession = !_authContext || _authContext.schema !== ctx.schema;
  _authContext = { token: ctx.token, schema: ctx.schema };
  console.log('[user_events_debug] setAuthContext set', {
    schema: ctx.schema, tokenLen: ctx.token.length, isFreshSession,
  });
  if (isFreshSession) {
    _sessionStartedAt = Date.now();
    startIdleTracking();
    _startHeartbeat();
    authReadyListeners.forEach((fn) => { try { fn(); } catch (_) { /* listener error is not ours */ } });
  }
}

/** Called once per fresh session (first auth context, or a schema switch). */
export function onAuthContextReady(fn) {
  authReadyListeners.add(fn);
  return () => authReadyListeners.delete(fn);
}

export function clearAuthContext() {
  console.log('[user_events_debug] clearAuthContext called');
  _authContext = null;
  _sessionStartedAt = null;
  _stopHeartbeat();
}

function _endpoint() {
  const base = process.env.REACT_APP_BACKEND_URL || getBackendURL() || '';
  return `${base}/api/user-events/batch/`;
}

function _headers() {
  if (!_authContext) return null;
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${_authContext.token}`,
    'X-Schema': _authContext.schema,
  };
}

function _scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushUserEvents();
  }, FLUSH_INTERVAL_MS);
}

function _attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushUserEvents();
  });
  window.addEventListener('beforeunload', () => {
    const now = Date.now();
    const wall_ms = _sessionStartedAt ? now - _sessionStartedAt : null;
    const active_ms = _sessionStartedAt ? getActiveMsSince(_sessionStartedAt, now) : null;
    const from = lastHeartbeatAt || _sessionStartedAt;
    const tail_active_ms = from ? getActiveMsSince(from, now) : 0;
    if (tail_active_ms > 0) {
      buffer.push({
        event_type: 'session_heartbeat',
        page: null,
        duration_ms: tail_active_ms,
        metadata: { interval_ms: HEARTBEAT_INTERVAL_MS, final: true, session_id: getSessionId() },
        client_timestamp: new Date().toISOString(),
      });
    }
    buffer.push({
      event_type: 'session_end',
      page: null,
      duration_ms: active_ms,
      metadata: {
        reason: 'tab_close',
        wall_ms,
        idle_ms: wall_ms != null && active_ms != null ? Math.max(0, wall_ms - active_ms) : null,
        session_id: getSessionId(),
      },
      client_timestamp: new Date().toISOString(),
    });
    flushUserEvents({ useBeacon: true });
  });
}

export function trackUserEvent(eventType, { page, duration_ms, metadata } = {}) {
  const headers = _headers();
  if (!headers) {
    console.warn('[user_events_debug] trackUserEvent dropped — no auth context', { eventType });
    return;
  }
  _attachListeners();
  buffer.push({
    event_type: eventType,
    page: page || null,
    duration_ms: duration_ms == null ? null : Math.round(duration_ms),
    metadata: { ...(metadata || {}), session_id: getSessionId() },
    client_timestamp: new Date().toISOString(),
  });
  if (buffer.length >= BATCH_SIZE) flushUserEvents();
  else _scheduleFlush();
}

function _requeue(events) {
  buffer = events.concat(buffer);
  if (buffer.length > MAX_BUFFER) {
    const dropped = buffer.length - MAX_BUFFER;
    buffer = buffer.slice(dropped);
    console.warn('[user_events_debug] buffer capped', { dropped, kept: MAX_BUFFER });
  }
  _scheduleFlush();
}

// `unload` (alias `useBeacon`, kept for existing callers) marks the tab-close
// flush. It used to go through navigator.sendBeacon, which cannot carry the
// Authorization / X-Schema headers and therefore always got a 401 — so
// session_end never persisted. A keepalive fetch carries headers and survives
// the page teardown in every current browser. keepalive requests are capped
// at 64 KiB by the Fetch spec, so unload sends at most one FLUSH_CHUNK-sized
// chunk (no chaining) and leaves the rest for the next flush.
export function flushUserEvents(opts = {}) {
  const { unload = false, useBeacon = false } = opts;
  const isUnload = unload || useBeacon;
  const headers = _headers();
  if (!headers) {
    if (buffer.length > 0) {
      console.warn('[user_events_debug] flush dropped — no auth context', {
        droppedCount: buffer.length,
      });
    }
    buffer = [];
    return Promise.resolve();
  }
  if (buffer.length === 0) return Promise.resolve();
  const sending = buffer.slice(0, FLUSH_CHUNK);
  buffer = buffer.slice(FLUSH_CHUNK);
  const eventCount = sending.length;
  const eventTypes = sending.reduce((acc, e) => {
    acc[e.event_type] = (acc[e.event_type] || 0) + 1;
    return acc;
  }, {});
  const payload = JSON.stringify({ events: sending });
  const endpoint = _endpoint();
  const schema = _authContext?.schema;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  return fetch(endpoint, {
    method: 'POST',
    headers,
    body: payload,
    keepalive: isUnload,
  })
    .then(async (resp) => {
      let bodyText = '';
      try { bodyText = await resp.text(); } catch {}
      if (!resp.ok) {
        if (resp.status >= 500) _requeue(sending);
        console.error('[user_events_debug] flush failed', {
          endpoint, schema, eventCount, unload: isUnload, status: resp.status,
          requeued: resp.status >= 500, body: bodyText.slice(0, 500),
        });
        return;
      }
      let parsed = null;
      try { parsed = bodyText ? JSON.parse(bodyText) : null; } catch {}
      console.log('[user_events_debug] flush ok', {
        endpoint, schema, eventCount, eventTypes, unload: isUnload, status: resp.status, response: parsed,
      });
      // Drain the rest of a backlog in further ≤FLUSH_CHUNK requests. Never
      // chain on an unload flush — the page is going away, so one chunk goes
      // out and anything left just waits (or gets capped by MAX_BUFFER).
      if (!isUnload && buffer.length > 0) {
        return flushUserEvents(opts);
      }
    })
    .catch((err) => {
      _requeue(sending);
      console.error('[user_events_debug] flush network error', {
        endpoint, schema, eventCount, unload: isUnload, requeued: true, error: String(err), name: err?.name,
      });
    });
}

export async function withAIQueryTracking(feature, fetchPromiseFactory) {
  const startedAt = Date.now();
  let success = false;
  let http_status = null;
  try {
    const resp = await fetchPromiseFactory();
    http_status = resp?.status ?? null;
    success = !!(resp && resp.ok);
    return resp;
  } finally {
    trackUserEvent('ai_query', {
      duration_ms: Date.now() - startedAt,
      metadata: { feature, success, http_status },
    });
  }
}

/**
 * Presence heartbeat for the Live Activity dashboard: current page, raw path,
 * activity flags, and the per-tab session. Flushed immediately so the server
 * sees a page change within a second instead of after the 30s batch timer.
 */
export function trackPresencePing({
  page, fullPath, activities = [], active = true, visible = true, reason = 'interval',
} = {}) {
  if (!_headers()) return Promise.resolve();
  trackUserEvent('presence_ping', {
    page: page || null,
    metadata: {
      session_started_at: getSessionStartedAt(),
      full_path: fullPath || null,
      activities: Array.isArray(activities) ? activities : [],
      active: !!active,
      visible: !!visible,
      reason,
    },
  });
  return flushUserEvents();
}

export function _resetForTests() {
  buffer = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  lastHeartbeatAt = null;
  listenersAttached = false;
  _authContext = null;
  _sessionStartedAt = null;
  authReadyListeners.clear();
}

export function _bufferForTests() {
  return buffer.slice();
}

export function _requeueForTests(events) {
  _requeue(events);
}
